'use client';

import { parseDate } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth-store';
import { useEventPortalStore } from '@/store/event-portal-store';
import type { EFGEvent } from '@/types';

// ============================================================================
// Types
// ============================================================================

interface ProcessedEvent extends Omit<EFGEvent, 'sort_date' | 'display_date'> {
  sort_status: number;
  sort_date: string | Date;
  display_date: string;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Capitalize first letter of a string
 */
function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Process events to add computed fields for sorting and display.
 * Ported from src/pages/Events.tsx lines 42-114
 */
function processEvents(events: EFGEvent[]): ProcessedEvent[] {
  const statusSortMap: Record<string, number> = {
    new: 0,
    review: 1,
    eligible: 2,
    planning: 3,
    approved: 4,
    'in progress': 5,
    'event ending': 6,
    'event ended': 7,
    ineligible: 99,
    revise: 100,
    canceled: 101,
  };

  return events.map((event) => {
    const { start_date, eligibility_custom_data, status } = event;

    // Parse custom data for requested date info
    let requested_date: string | undefined;
    let requested_month: string | undefined;

    try {
      const customData = JSON.parse(eligibility_custom_data || '{}');
      requested_date = customData.requested_date;
      requested_month = customData.requested_month;
    } catch {
      // Handle invalid JSON gracefully
    }

    // Determine sort status based on status string
    const sort_status = statusSortMap[status || ''] ?? 999;

    // Process date for sorting and display
    let sort_date: string | Date;
    let display_date: string;

    if (start_date) {
      sort_date = start_date;
      display_date = format(parseDate(start_date), 'EEE MMM do yyyy');
    } else if (requested_date) {
      sort_date = requested_date;
      display_date = format(parseDate(requested_date), 'EEE MMM do yyyy');
    } else {
      // Fallback to requested month string
      sort_date = requested_month || '';
      display_date = requested_month || '';
    }

    return {
      ...event,
      sort_status,
      sort_date,
      display_date,
    };
  });
}

// ============================================================================
// Column Helper
// ============================================================================

const columnHelper = createColumnHelper<ProcessedEvent>();

// ============================================================================
// Events Page Component
// ============================================================================

export default function EventsPage() {
  const router = useRouter();
  const { dealer } = useAuthStore();
  const { setEvent, setEventToken } = useEventPortalStore();

  // Fetch events list
  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      const { result } = await api.getEvents();
      const events = Array.isArray(result) ? result : result?.events || [];
      return processEvents(events);
    },
  });

  // Define table columns
  const columns = [
    columnHelper.accessor('charity_partner', {
      header: 'Charity Partner',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => capitalize(info.getValue() || ''),
    }),
    columnHelper.accessor('display_date', {
      header: 'Event Date',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const { event_id, status } = row.original;

        // Event portal access for approved through event ended statuses
        if (['approved', 'in progress', 'event ending', 'event ended'].includes(status || '')) {
          return (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => router.push(`/event/${event_id}`)}>
                Event Portal
              </Button>
            </div>
          );
        }

        return null;
      },
    }),
  ];

  // Initialize TanStack Table
  const table = useReactTable({
    data: events,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="container mx-auto py-8">
      {/* Header Section */}
      <div className="mb-6">
        <h3 className="mb-4 text-2xl font-bold">{dealer?.dealer_name}: Events</h3>
      </div>

      {/* Events Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    Program Headquarters will reach out to assist with your event. Once your event
                    is approved, details will appear here.
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
